'use client';

import React from 'react';
import Link from 'next/link';
import { MobileOnly, DesktopOnly } from './DeviceLayouts';
import { WhatsAppInlineButton } from '@/components/WhatsAppButton';
import TrackedCTALink from '@/app/components/TrackedCTALink';

type Breadcrumb = {
  label: string;
  href?: string;
};

type PageHeroProps = {
  breadcrumbs?: Breadcrumb[];
  badge?: string;
  title: string;
  description?: string;
  secondaryDescription?: string;
  chips?: string[];
  phoneLabel?: string;
  phoneHref?: string;
  quoteHref?: string;
  quoteLabel?: string;
  whatsappLabel?: string;
  whatsappMessage?: string;
};

const trustPoints = [
  { label: 'Diagnose eerst', value: 'Geen onnodige vervanging' },
  { label: '1 jaar garantie', value: 'Op uitgevoerde werken' },
  { label: 'Alle merken', value: 'Personenwagens en bestelwagens' },
  { label: 'Lokeren', value: 'Vlak bij de E17' },
];

export default function PageHero({
  breadcrumbs = [],
  badge,
  title,
  description,
  secondaryDescription,
  chips = [],
  phoneLabel = 'BEL NU: 0498 54 54 74',
  phoneHref = 'tel:0498545474',
  quoteHref = '/offerte',
  quoteLabel = 'Gratis Offerte',
  whatsappLabel = 'Stuur een bericht',
  whatsappMessage = 'Hallo, ik wil graag meer informatie over mijn roetfilter.',
}: PageHeroProps) {
  return (
    <section className="relative overflow-hidden rc-hero-pattern text-white">
      <MobileOnly>
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-black/15" />
        <div className="relative px-4 py-12">
          {breadcrumbs.length > 0 && (
            <nav className="mb-5 text-xs text-white/70">
              {breadcrumbs.map((item, index) => (
                <React.Fragment key={`${item.label}-${index}`}>
                  {item.href ? (
                    <Link href={item.href} className="hover:text-white">
                      {item.label}
                    </Link>
                  ) : (
                    <span>{item.label}</span>
                  )}
                  {index < breadcrumbs.length - 1 && <span className="mx-2">›</span>}
                </React.Fragment>
              ))}
            </nav>
          )}

          {badge && (
            <div className="mb-4 inline-flex rounded-full border border-white/25 bg-white/10 px-4 py-2 text-xs font-bold uppercase tracking-wide text-accent">
              {badge}
            </div>
          )}

          <h1 className="safe-heading text-[31px] font-black leading-[1.03] tracking-[-0.045em]">
            {title}
          </h1>

          {description && (
            <p className="safe-copy mt-5 text-base leading-[1.6] text-white/90">
              {description}
            </p>
          )}

          {secondaryDescription && (
            <p className="safe-copy mt-3 text-sm leading-[1.55] text-white/75">
              {secondaryDescription}
            </p>
          )}

          {chips.length > 0 && (
            <div className="mt-6 flex flex-wrap gap-2">
              {chips.map((chip) => (
                <span
                  key={chip}
                  className="rounded-full border border-white/25 bg-white/10 px-3 py-1.5 text-xs font-semibold text-white"
                >
                  {chip}
                </span>
              ))}
            </div>
          )}

          <div className="mt-7 flex flex-col gap-3">
            <TrackedCTALink
              href={phoneHref}
              location="page-hero-mobile"
              type="phone"
              className="rc-cta-primary flex w-full items-center justify-center px-6 py-4 text-sm font-extrabold shadow-lg"
            >
              📞 {phoneLabel}
            </TrackedCTALink>

            <Link
              href={quoteHref}
              className="flex w-full items-center justify-center rounded-full border-2 border-white/35 bg-white/15 px-6 py-4 text-sm font-bold text-white"
            >
              {quoteLabel}
            </Link>

            <WhatsAppInlineButton
              label={whatsappLabel}
              message={whatsappMessage}
              location="page-hero-mobile"
              className="w-full justify-center px-6 py-4 text-sm"
            />
          </div>
        </div>
      </MobileOnly>

      <DesktopOnly>
        {/* Background overlays */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-black/10 to-black/20" />
        <div className="absolute inset-0 grid-dots opacity-30" />

        <div className="relative mx-auto max-w-7xl px-8 py-20 xl:px-10 xl:py-24">
          <div className="grid grid-cols-[minmax(0,1fr)_360px] gap-12 items-center">
            {/* Left column */}
            <div>
              {breadcrumbs.length > 0 && (
                <nav className="mb-7 text-sm text-white/70">
                  {breadcrumbs.map((item, index) => (
                    <React.Fragment key={`${item.label}-${index}`}>
                      {item.href ? (
                        <Link href={item.href} className="hover:text-white">
                          {item.label}
                        </Link>
                      ) : (
                        <span>{item.label}</span>
                      )}
                      {index < breadcrumbs.length - 1 && <span className="mx-2">›</span>}
                    </React.Fragment>
                  ))}
                </nav>
              )}

              {badge && (
                <div className="rc-kicker mb-5 border-white/25 bg-white/10 text-accent">
                  {badge}
                </div>
              )}

              <h1 className="safe-heading max-w-5xl text-[44px] font-black leading-[0.98] tracking-[-0.05em] xl:text-[56px]">
                {title}
              </h1>

              {description && (
                <p className="safe-copy mt-6 max-w-3xl text-xl leading-[1.6] text-white/90">
                  {description}
                </p>
              )}

              {secondaryDescription && (
                <p className="safe-copy mt-4 max-w-3xl text-base leading-[1.55] text-white/75">
                  {secondaryDescription}
                </p>
              )}

              {chips.length > 0 && (
                <div className="mt-8 flex max-w-4xl flex-wrap gap-3">
                  {chips.map((chip) => (
                    <span
                      key={chip}
                      className="rounded-full border border-white/30 bg-white/10 px-4 py-2 text-sm font-semibold text-white"
                    >
                      {chip}
                    </span>
                  ))}
                </div>
              )}

              <div className="mt-9 flex flex-wrap gap-4">
                <TrackedCTALink
                  href={phoneHref}
                  location="page-hero-desktop"
                  type="phone"
                  className="rc-cta-primary inline-flex items-center justify-center px-8 py-4 text-base font-extrabold shadow-xl"
                >
                  📞 {phoneLabel}
                </TrackedCTALink>

                <Link
                  href={quoteHref}
                  className="inline-flex items-center justify-center rounded-full border-2 border-white/35 bg-white/15 px-8 py-4 text-base font-bold text-white transition-colors hover:bg-white/25"
                >
                  {quoteLabel}
                </Link>

                <WhatsAppInlineButton
                  label={whatsappLabel}
                  message={whatsappMessage}
                  location="page-hero-desktop"
                  className="px-8 py-4 text-base"
                />
              </div>
            </div>

            {/* Right column — trust card */}
            <div className="rounded-[28px] border border-white/20 bg-black/25 backdrop-blur-sm p-6 shadow-2xl">
              <p className="text-xs font-black uppercase tracking-[0.18em] text-accent mb-5">
                Waarom Roetfilter Center?
              </p>

              <div className="space-y-3">
                {trustPoints.map((point) => (
                  <div key={point.label} className="rounded-2xl border border-white/10 bg-white/10 p-4">
                    <p className="text-sm font-extrabold text-white">
                      {point.label}
                    </p>
                    <p className="mt-1 text-xs leading-relaxed text-white/70">
                      {point.value}
                    </p>
                  </div>
                ))}
              </div>

              <div className="mt-5 rounded-2xl border border-accent/30 bg-accent/10 p-4">
                <p className="text-sm font-black text-accent">
                  4.9/5 klantwaardering
                </p>
                <p className="mt-1 text-xs leading-relaxed text-white/70">
                  Meer dan 2.000 tevreden klanten geholpen met diagnose, reiniging, revisie en vervanging.
                </p>
              </div>
            </div>
          </div>
        </div>
      </DesktopOnly>
    </section>
  );
}
